/**
 * Messagerie formateur ↔ parents.
 *
 * Le formateur peut écrire à un parent (en ciblant un étudiant) ou en broadcast
 * à tous les parents d'une classe.
 */
import { useState, useMemo } from 'react';
import {
  MessageSquare, Loader2, Plus, X, Send, Users, User, ChevronDown,
} from 'lucide-react';
import { FormateurLayout } from '../FormateurLayout';
import {
  useListerMessagesParentsQuery, useEnvoyerMessageParentMutation,
  useFormateurMeQuery, useMesEtudiantsQuery,
} from '../../services/formateurApi';

export default function MessagesParents() {
  const { data: liste, isLoading, refetch } = useListerMessagesParentsQuery();
  const [showNouveau, setShowNouveau] = useState(false);

  return (
    <FormateurLayout title="Messages aux parents">
      <button onClick={() => setShowNouveau(true)}
        className="w-full mb-3 bg-gradient-to-br from-cyan-900/40 to-cyan-900/10 hover:border-cyan-500
          border border-cyan-700/40 rounded-xl p-3 flex items-center gap-3 transition-colors text-left">
        <div className="w-9 h-9 rounded-lg bg-black/30 flex items-center justify-center text-cyan-300">
          <Plus size={18} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-white">Nouveau message</div>
          <div className="text-[11px] text-gray-400">À un parent ou à toute une classe</div>
        </div>
      </button>

      <h2 className="text-[11px] uppercase tracking-wider font-bold text-gray-400 mb-2">
        Messages envoyés ({liste?.total || 0})
      </h2>

      {isLoading ? (
        <div className="text-center py-6 text-gray-500 text-sm">
          <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement…
        </div>
      ) : (liste?.results || []).length === 0 ? (
        <div className="text-center py-6 text-gray-500 text-sm">
          Aucun message envoyé.
        </div>
      ) : (
        <div className="space-y-1.5">
          {liste!.results.map(m => {
            const broadcast = m.portee === 'classe';
            return (
              <div key={m.id} className={`rounded-lg border p-2.5 ${
                broadcast
                  ? 'bg-amber-900/15 border-amber-800/60'
                  : 'bg-gray-900 border-gray-800'
              }`}>
                <div className="flex items-start gap-2 mb-1">
                  <div className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 ${
                    broadcast ? 'bg-amber-900/40 text-amber-300' : 'bg-cyan-900/40 text-cyan-300'
                  }`}>
                    {broadcast ? <Users size={12} /> : <User size={12} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-white truncate">
                      {m.sujet || '(Sans sujet)'}
                    </div>
                    <div className="text-[10px] text-gray-500 truncate">
                      {broadcast
                        ? `Tous les parents de ${m.classe}`
                        : `Parents de ${m.matricule_etudiant_cible}`}
                      {' • '}
                      {new Date(m.date_envoi).toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                      })}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-300 line-clamp-3 mt-1">{m.contenu}</div>
                {m.matiere_libelle && (
                  <div className="text-[10px] text-gray-500 mt-1">Matière : {m.matiere_libelle}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showNouveau && (
        <ModalNouveau onClose={() => setShowNouveau(false)}
          onDone={() => { setShowNouveau(false); refetch(); }} />
      )}
    </FormateurLayout>
  );
}

function ModalNouveau({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { data: me } = useFormateurMeQuery();
  const [mode, setMode] = useState<'individuel' | 'classe'>('individuel');
  const [classe, setClasse] = useState<string>('');
  const [matricule, setMatricule] = useState('');
  const [sujet, setSujet] = useState('');
  const [contenu, setContenu] = useState('');
  const [matiere, setMatiere] = useState('');
  const [erreur, setErreur] = useState('');

  const { data: etudiantsData } = useMesEtudiantsQuery(
    mode === 'individuel' && classe ? { classe } : undefined,
    { skip: mode !== 'individuel' || !classe },
  );
  const etudiants = etudiantsData?.results || [];

  const [envoyer, { isLoading }] = useEnvoyerMessageParentMutation();

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur('');
    if (!contenu.trim()) { setErreur('Message requis'); return; }
    if (mode === 'individuel' && !matricule) { setErreur('Sélectionnez un étudiant'); return; }
    if (mode === 'classe' && !classe) { setErreur('Sélectionnez une classe'); return; }
    try {
      await envoyer({
        matricule_etudiant_cible: mode === 'individuel' ? matricule : undefined,
        classe: mode === 'classe' ? classe : undefined,
        sujet, contenu, matiere_libelle: matiere || undefined,
      }).unwrap();
      onDone();
    } catch (err: any) {
      setErreur(err?.data?.error || err?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center"
      onClick={onClose}>
      <form onSubmit={handle} onClick={(e) => e.stopPropagation()}
        className="bg-gray-900 border border-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto">
        <header className="sticky top-0 bg-gray-900 border-b border-gray-800 px-4 py-3 flex items-center justify-between">
          <h3 className="font-bold text-base text-white inline-flex items-center gap-2">
            <MessageSquare size={16} className="text-cyan-400" /> Nouveau message aux parents
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </header>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setMode('individuel')}
              className={`px-3 py-2 text-xs rounded border inline-flex items-center justify-center gap-1.5 ${
                mode === 'individuel'
                  ? 'bg-cyan-600 text-white border-cyan-600'
                  : 'bg-gray-800 text-gray-300 border-gray-700'
              }`}>
              <User size={12} /> Un parent
            </button>
            <button type="button" onClick={() => setMode('classe')}
              className={`px-3 py-2 text-xs rounded border inline-flex items-center justify-center gap-1.5 ${
                mode === 'classe'
                  ? 'bg-amber-600 text-white border-amber-600'
                  : 'bg-gray-800 text-gray-300 border-gray-700'
              }`}>
              <Users size={12} /> Toute la classe
            </button>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">Classe *</label>
            <div className="relative">
              <select value={classe} onChange={(e) => { setClasse(e.target.value); setMatricule(''); }}
                className="w-full appearance-none px-3 py-2 pr-8 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100">
                <option value="">— Choisir —</option>
                {(me?.classes || []).map(c => (
                  <option key={c.code} value={c.code}>{c.code}</option>
                ))}
              </select>
              <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500" />
            </div>
          </div>

          {mode === 'individuel' && classe && (
            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1">Étudiant *</label>
              <div className="relative">
                <select value={matricule} onChange={(e) => setMatricule(e.target.value)}
                  className="w-full appearance-none px-3 py-2 pr-8 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100">
                  <option value="">— Choisir —</option>
                  {etudiants.map(e => (
                    <option key={e.matricule} value={e.matricule}>
                      {e.prenoms} {e.nom} ({e.matricule})
                    </option>
                  ))}
                </select>
                <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500" />
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                Tous les parents liés à cet étudiant recevront le message.
              </p>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">Matière (optionnel)</label>
            <input value={matiere} onChange={(e) => setMatiere(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">Sujet</label>
            <input value={sujet} onChange={(e) => setSujet(e.target.value)} maxLength={200}
              className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">Message *</label>
            <textarea value={contenu} onChange={(e) => setContenu(e.target.value)}
              rows={5} required maxLength={5000}
              className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100 resize-none" />
          </div>

          {erreur && (
            <div className="bg-red-900/30 border border-red-800 text-red-200 rounded p-2 text-xs">
              {erreur}
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-gray-900 border-t border-gray-800 px-4 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-800 rounded">
            Annuler
          </button>
          <button type="submit" disabled={isLoading || !contenu.trim()}
            className="px-4 py-1.5 text-xs bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white font-semibold rounded inline-flex items-center gap-1.5">
            {isLoading ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            {isLoading ? 'Envoi…' : 'Envoyer'}
          </button>
        </footer>
      </form>
    </div>
  );
}
